import { Button, Checkbox, Input, Select, SelectItem, Table, TableBody, TableCell, TableColumn, TableHeader, TableRow } from '@heroui/react';
import { Search, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ContentLoadError, PageTitle, Spinner } from '../../../components';
import { useHeroToast } from '../../../hooks';
import {
  useAssignOrganizationAttractions,
  useGetAttractionsFromFirestore,
  useGetOrganization,
} from '../../../queries';

export const AssignAttractions = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useHeroToast();

  const [searchQuery, setSearchQuery] = useState('');
  const [cityFilter, setCityFilter] = useState<string>('');
  const [selectedAttractions, setSelectedAttractions] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);

  // Fetch organization data
  const { data, isLoading: isOrgLoading, error: orgError } = useGetOrganization(id || '');
  const organization = data?.organization;
  const { mutateAsync: saveAttractions } = useAssignOrganizationAttractions();

  // Fetch all attractions. The hook drives loading via the inner data
  // payload (`data.isLoading`) because it bridges react-query with a
  // realtime Firestore listener — react-query's own isLoading is not a
  // reliable signal here.
  const { data: attractionsData, error: attractionsError } = useGetAttractionsFromFirestore();
  const isAttractionsLoading = !attractionsData || attractionsData.isLoading;
  const effectiveAttractionsError = attractionsError || attractionsData?.error || null;

  useEffect(() => {
    const ids = organization?.associated_attractions ?? [];
    setSelectedAttractions(new Set(ids.filter(Boolean)));
  }, [organization]);

  // Unique, sorted list of cities present in the attractions list — only
  // cities with at least one attraction show up in the dropdown.
  const cityOptions = useMemo(() => {
    if (!attractionsData?.data) return [] as string[];
    const set = new Set<string>();
    attractionsData.data.forEach((a) => {
      if (typeof a.city === 'string' && a.city.trim()) {
        set.add(a.city.trim());
      }
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'pl'));
  }, [attractionsData]);

  // Filter attractions based on search across all title locales + city,
  // address, type, email. City filter narrows the list to a single city.
  const filteredAttractions = useMemo(() => {
    if (!attractionsData?.data) return [];

    const searchLower = searchQuery.trim().toLowerCase();
    const cityLower = cityFilter.trim().toLowerCase();

    return attractionsData.data.filter((attraction) => {
      if (cityLower && (attraction.city || '').toLowerCase() !== cityLower) {
        return false;
      }

      if (!searchLower) return true;

      const titleValues = attraction.title
        ? Object.values(attraction.title).filter((v): v is string => typeof v === 'string')
        : [];

      const haystacks = [
        ...titleValues,
        attraction.city || '',
        attraction.address || '',
        attraction.type || '',
        attraction.email || '',
      ];

      return haystacks.some((h) => h.toLowerCase().includes(searchLower));
    });
  }, [attractionsData, searchQuery, cityFilter]);

  const handleSelectionChange = (attractionId: string, isSelected: boolean) => {
    const newSelection = new Set(selectedAttractions);
    if (isSelected) {
      newSelection.add(attractionId);
    } else {
      newSelection.delete(attractionId);
    }
    setSelectedAttractions(newSelection);
  };

  const handleSelectAll = () => {
    if (selectedAttractions.size === filteredAttractions.length) {
      // Deselect all
      setSelectedAttractions(new Set());
    } else {
      // Select all filtered
      const allIds = filteredAttractions.map((a) => a.id);
      setSelectedAttractions(new Set(allIds));
    }
  };

  const handleSave = async () => {
    if (!id || !organization) {
      showError('Brak ID organizacji');
      return;
    }

    setIsSaving(true);
    try {
      const attractionIds = Array.from(selectedAttractions);

      console.log('Saving attractions:', {
        organizationId: id,
        attractionIds,
        count: attractionIds.length,
      });

      await saveAttractions({ id, attractionIds });

      showSuccess(`Przypisano ${attractionIds.length} atrakcji do organizacji`);

      // Small delay to ensure updates propagate
      setTimeout(() => {
        navigate('/organizations');
      }, 300);
    } catch (error: any) {
      console.error('Error updating organization:', error);
      showError(`Błąd podczas zapisywania atrakcji: ${error?.message || 'Nieznany błąd'}`);
    } finally {
      setIsSaving(false);
    }
  };

  if (isOrgLoading || isAttractionsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner showLabel />
      </div>
    );
  }

  if (orgError || effectiveAttractionsError) {
    return (
      <ContentLoadError
        message="Błąd podczas ładowania danych"
        onRefreshClicked={() => window.location.reload()}
      />
    );
  }

  if (!organization) {
    return (
      <ContentLoadError
        message="Organizacja nie została znaleziona"
        onRefreshClicked={() => navigate('/organizations')}
      />
    );
  }

  return (
    <div className="p-6">
      <PageTitle
        title={`Przypisz atrakcje - ${organization.hotel || organization.name}`}
        buttonText="Wróć"
        buttonOnClick={() => navigate('/organizations')}
      />

      <div className="mt-6 space-y-4">
        {/* Search and stats */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2 flex-1 flex-wrap">
            <Input
              placeholder="Szukaj po nazwie, mieście, adresie..."
              startContent={<Search size={18} />}
              value={searchQuery}
              onValueChange={setSearchQuery}
              isClearable
              onClear={() => setSearchQuery('')}
              classNames={{
                base: 'max-w-md min-w-[260px]',
                input: 'text-sm',
              }}
            />

            <div className="flex items-center gap-1">
              <Select
                aria-label="Filtruj po mieście"
                placeholder="Wszystkie miasta"
                size="sm"
                selectedKeys={cityFilter ? new Set([cityFilter]) : new Set()}
                onSelectionChange={(keys) => {
                  const value = Array.from(keys as Set<string>)[0] ?? '';
                  setCityFilter(value);
                }}
                isDisabled={cityOptions.length === 0}
                className="min-w-[200px]"
                classNames={{ trigger: 'h-10' }}
              >
                {cityOptions.map((city) => (
                  <SelectItem key={city}>{city}</SelectItem>
                ))}
              </Select>
              {cityFilter && (
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  aria-label="Wyczyść filtr miasta"
                  onPress={() => setCityFilter('')}
                >
                  <X size={14} />
                </Button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">
              Wybrano:
              {' '}
              <span className="font-semibold text-primary">{selectedAttractions.size}</span>
              {' '}
              /
              {' '}
              {filteredAttractions.length}
            </span>

            <Button
              color="primary"
              variant="flat"
              size="sm"
              onPress={handleSelectAll}
            >
              {selectedAttractions.size === filteredAttractions.length ? 'Odznacz wszystkie' : 'Zaznacz wszystkie'}
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="border rounded-lg overflow-hidden">
          <Table
            aria-label="Attractions table"
            classNames={{
              base: 'max-h-[600px] overflow-y-auto',
              table: 'min-h-[400px]',
            }}
          >
            <TableHeader>
              <TableColumn key="select" width="50">
                <Checkbox
                  isSelected={filteredAttractions.length > 0 && selectedAttractions.size === filteredAttractions.length}
                  isIndeterminate={selectedAttractions.size > 0 && selectedAttractions.size < filteredAttractions.length}
                  onValueChange={() => handleSelectAll()}
                />
              </TableColumn>
              <TableColumn key="title">Nazwa</TableColumn>
              <TableColumn key="city">Miasto</TableColumn>
              <TableColumn key="type">Typ</TableColumn>
            </TableHeader>
            <TableBody emptyContent="Brak atrakcji do wyświetlenia">
              {filteredAttractions.map((attraction) => (
                <TableRow key={attraction.id}>
                  <TableCell>
                    <Checkbox
                      isSelected={selectedAttractions.has(attraction.id)}
                      onValueChange={(isSelected) => handleSelectionChange(attraction.id, isSelected)}
                    />
                  </TableCell>
                  <TableCell>
                    <span className="font-medium">
                      {attraction.title?.pl || attraction.title?.en || 'Bez nazwy'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-gray-600">
                      {attraction.city || '-'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-gray-500">
                      {attraction.type || '-'}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Action buttons */}
        <div className="flex justify-end gap-3 pt-4">
          <Button
            variant="light"
            onPress={() => navigate('/organizations')}
            isDisabled={isSaving}
          >
            Anuluj
          </Button>
          <Button
            color="primary"
            onPress={handleSave}
            isLoading={isSaving}
          >
            Zapisz przypisane atrakcje (
            {selectedAttractions.size}
            )
          </Button>
        </div>
      </div>
    </div>
  );
};
